import { memo } from 'react';
import { type EdgeProps, getSmoothStepPath, EdgeLabelRenderer } from '@xyflow/react';
import { ENTRANCE_CATEGORY_COLORS } from '../data/entrance-types';
import type { EntranceCategory } from '../data/entrance-types';

export interface TrackedEdgeData {
  category: EntranceCategory;
  label?: string;
  isOneWay?: boolean;
  isBidirectional?: boolean;
  [key: string]: unknown;
}

function TrackedEdgeComponent({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
  selected,
}: EdgeProps) {
  const { category, label, isOneWay, isBidirectional } = (data ?? {}) as TrackedEdgeData;
  const color = ENTRANCE_CATEGORY_COLORS[category] ?? '#6b7280';

  const [edgePath, labelX, labelY] = getSmoothStepPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    borderRadius: 8,
  });

  return (
    <>
      <path
        id={id}
        className="react-flow__edge-path"
        d={edgePath}
        style={{
          stroke: color,
          strokeWidth: selected ? 2.5 : 1.5,
          strokeDasharray: isOneWay ? '6 3' : undefined,
          opacity: selected ? 1 : 0.6,
        }}
        markerEnd={`url(#arrow-${category})`}
        markerStart={isBidirectional ? `url(#arrow-${category})` : undefined}
      />
      {label && (
        <EdgeLabelRenderer>
          <div
            className="absolute text-[9px] bg-gray-900/80 text-gray-300 px-1 rounded pointer-events-none"
            style={{
              transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
            }}
          >
            {label}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
}

export const TrackedEdge = memo(TrackedEdgeComponent);

/** SVG marker definitions for edge arrowheads */
export function EdgeMarkerDefs() {
  return (
    <svg className="absolute w-0 h-0">
      <defs>
        {Object.entries(ENTRANCE_CATEGORY_COLORS).map(([category, color]) => (
          <marker
            key={category}
            id={`arrow-${category}`}
            viewBox="0 0 10 10"
            refX="10"
            refY="5"
            markerWidth="8"
            markerHeight="8"
            orient="auto-start-reverse"
          >
            <path d="M 0 0 L 10 5 L 0 10 z" fill={color} />
          </marker>
        ))}
      </defs>
    </svg>
  );
}
